import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Gift, Truck, Calendar, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const SHIPPING = 10.99;

const REVEAL_ITEMS = [
  { name: "Personalized Quilted Puff Tote", desc: "Embroidered with her initials." },
  { name: "Matching Cosmetic Pouch", desc: "A stylish companion for her everyday essentials." },
  { name: "unplugged. Tee", desc: "A reminder to disconnect and recharge." },
  { name: "off duty. Tee", desc: "Because she doesn't have to do it all, all the time." },
  { name: "Temperature-Display Stainless Steel Tumbler", desc: "Stainless steel interior with a digital temperature display on the lid." },
  { name: "Cozy Socks", desc: "A little extra comfort for her downtime." },
  { name: 'A Special "Just Because" Surprise Gift', desc: "A little something extra, tucked inside just for her!" },
];

const HerTurn = () => {
  const { addItem } = useCart();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState({});
  const [size, setSize] = useState("");
  const [monogram, setMonogram] = useState("");
  const [revealOpen, setRevealOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/products/her-turn-box").then((r) => {
      setProduct(r.data);
      setSize((r.data.sizes || [])[0] || "");
    }).catch(() => {});
    api.get("/site-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const openDate = config.herturn_open_date ? new Date(config.herturn_open_date) : null;
  const preordersOpen = openDate ? new Date() >= openDate : false;
  const openDateLabel = openDate
    ? openDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "October 30";
  const shipDate = config.herturn_ship_date || "December 6";

  const handlePreorder = () => {
    if (!product) return;
    if ((product.sizes || []).length && !size) {
      toast.error("Please select a shirt size");
      return;
    }
    addItem(product, {
      size,
      quantity: 1,
      monogram: monogram.trim().toUpperCase().slice(0, 3) || null,
      monogram_price: 0,
    });
    toast.success("Added The Her Turn Box to your bag");
    nav("/cart");
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/herturn/waitlist", { email, name });
      setJoined(true);
      toast.success("You're on the waitlist!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const heroImg = (product?.images || [])[0] || config.hero_image || "/hero/lu-homecoming.webp";
  const price = product?.price ?? 75.0;

  return (
    <main data-testid="herturn-page" className="bg-white">
      {/* HERO */}
      <section className="relative bg-[#F0EDE6]">
        <div className="container-x py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-black text-[#EC008C] mb-4">
              <Sparkles size={14} /> The Her Turn Box
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-black">
              Gifts with a little <span className="text-[#EC008C]">more meaning.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#4A4A4A] max-w-md leading-relaxed">
              A curated mystery box made with intention — a reminder that it's her turn.
              Reserve yours now; contents revealed before it ships.
            </p>

            <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold text-black">
              <span className="inline-flex items-center gap-2"><Gift size={16} className="text-[#EC008C]" /> ${price.toFixed(2)} box</span>
              <span className="inline-flex items-center gap-2"><Truck size={16} className="text-[#EC008C]" /> +${SHIPPING.toFixed(2)} shipping</span>
              <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-[#EC008C]" /> Ships {shipDate}</span>
            </div>

            <div className="mt-8">
              {preordersOpen ? (
                <div data-testid="herturn-preorder" className="brutal-static bg-white rounded-2xl p-5 max-w-md">
                  <div className="text-xs font-black uppercase tracking-widest text-[#333] mb-3">Pre-order now · Select shirt size</div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(product?.sizes || []).map((s) => (
                      <button
                        key={s}
                        data-testid={`herturn-size-${s}`}
                        onClick={() => setSize(s)}
                        className={`w-12 h-11 rounded-xl border-2 border-black font-bold text-sm ${
                          size === s ? "bg-[#EC008C] text-white" : "bg-white text-black"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <div className="mb-4">
                    <div className="text-xs font-black uppercase tracking-widest text-[#333] mb-1">Monogram the bag · Free</div>
                    <p className="text-[11px] text-[#777] mb-2">Add up to 3 initials embroidered on the quilted tote. Leave blank to skip.</p>
                    <input
                      data-testid="herturn-monogram"
                      value={monogram}
                      onChange={(e) => setMonogram(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 3))}
                      placeholder="e.g. MSG"
                      maxLength={3}
                      className="w-32 px-3 py-2 border-2 border-black rounded-xl font-black tracking-[0.3em] text-center uppercase"
                    />
                  </div>
                  <button
                    data-testid="herturn-preorder-btn"
                    onClick={handlePreorder}
                    className="w-full inline-flex items-center justify-center gap-2 bg-black text-white font-black rounded-full px-6 py-3 brutal"
                  >
                    <ShoppingBag size={18} /> Pre-order — ${price.toFixed(2)}
                  </button>
                  <p className="text-[11px] text-[#777] mt-2 text-center">${SHIPPING.toFixed(2)} flat shipping added at checkout · Ships {shipDate}</p>
                </div>
              ) : joined ? (
                <div data-testid="herturn-joined" className="brutal-static bg-[#CCDE00] rounded-2xl p-5 max-w-md">
                  <div className="font-display font-black text-xl flex items-center gap-2"><Check size={20} /> You're on the list!</div>
                  <p className="text-sm text-black/70 mt-1">We'll email you the moment pre-orders open on {openDateLabel}.</p>
                </div>
              ) : (
                <form onSubmit={handleWaitlist} data-testid="herturn-waitlist" className="brutal-static bg-white rounded-2xl p-5 max-w-md">
                  <div className="text-xs font-black uppercase tracking-widest text-[#333] mb-1">Join the waitlist</div>
                  <p className="text-sm text-[#4A4A4A] mb-3">Pre-orders open <strong>{openDateLabel}</strong>. Be first to know.</p>
                  <input
                    data-testid="herturn-waitlist-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="w-full mb-2 px-3 py-2.5 border-2 border-black rounded-xl"
                  />
                  <input
                    data-testid="herturn-waitlist-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full mb-3 px-3 py-2.5 border-2 border-black rounded-xl"
                  />
                  <button
                    data-testid="herturn-waitlist-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#EC008C] text-white font-black rounded-full px-6 py-3 brutal disabled:opacity-50"
                  >
                    {submitting ? "Joining…" : "Notify me when it opens"}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="aspect-[4/3] md:aspect-[4/5] overflow-hidden rounded-2xl border-2 border-black bg-white">
            <img data-testid="herturn-hero-img" src={heroImg} alt="The Her Turn Box" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="container-x py-16 md:py-24">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">What's inside</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-black">The reveal.</h2>
        </div>

        {!revealOpen ? (
          <button
            data-testid="herturn-reveal-card"
            onClick={() => setRevealOpen(true)}
            className="group block w-full max-w-xl mx-auto brutal bg-[#3A2A1E] text-white rounded-2xl p-10 text-center transition-transform hover:-translate-y-1"
          >
            <Gift size={44} className="mx-auto text-[#E8B88A] mb-4" />
            <div className="font-display font-black text-2xl">Tap to reveal what's inside</div>
            <p className="text-white/70 mt-2 text-sm">Go on — take a peek at The Her Turn Box.</p>
            <span className="inline-flex items-center gap-2 mt-5 bg-[#EC008C] text-white font-black rounded-full px-5 py-2 text-sm">
              <Sparkles size={15} /> Reveal the box
            </span>
          </button>
        ) : (
          <div data-testid="herturn-reveal-open" className="max-w-2xl mx-auto">
            <div className="brutal-static bg-white rounded-2xl overflow-hidden">
              <img
                data-testid="herturn-reveal-photo"
                src="/herturn/reveal.png"
                alt="Everything inside The Her Turn Box"
                className="w-full h-auto block border-b-2 border-black"
              />
              <div className="p-6 sm:p-8">
                <h3 className="font-display font-black text-2xl text-black flex items-center gap-2 mb-5">
                  <Gift size={22} className="text-[#EC008C]" /> What's inside the box?
                </h3>
                <div className="grid gap-4">
                  {REVEAL_ITEMS.map((it, i) => (
                    <div key={i} className="flex gap-3">
                      <Sparkles size={16} className="text-[#EC008C] mt-1 shrink-0" />
                      <p className="text-[15px] leading-relaxed text-black">
                        <strong>{it.name}</strong>
                        {it.desc ? <span className="text-[#4A4A4A]"> – {it.desc}</span> : null}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default HerTurn;
